import {
    useEffect,
    useMemo,
    type FC
} from "react";
import {
    Platform,
    View
} from "react-native";
import type ILocaleSwitcherProps from "./type";
import type {
    LocaleSwitcherDefaultKey,
    LocaleSwitchVariant
} from "./type";
import stylesheet, {
    LOCALE_SWITCHER_DEFAULT_KEYS,
    LOCALE_SWITCHER_ICON_COLORS,
    getLocaleSwitcherShortTitle,
    getLocaleSwitcherBadgeColor,
    LOCALE_SWITCHER_TITLE_KEYS,
    getLocaleSwitcherIconColor,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitDialog,
    NCoreUIKitTheme
} from "../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../types";
import {
    Languages as LanguagesIcon
} from "lucide-react-native";
import {
    getSwitcherBorderColor,
    getSwitcherVariants
} from "../../helpers/preference";
import {
    warnOnce
} from "../../utils/development";
import PreferenceChevronIcon from "../../helpers/preference/menu/chevron";
import PreferenceForm from "../../helpers/preference/form";
import Button from "../button";
import Text from "../text";

const LocaleSwitcher: FC<ILocaleSwitcherProps> = ({
    isShowTitle: isShowTitleProp,
    isWorkWithHighlightButton,
    isWorkWithNextShowSystem,
    variants: variantsProp,
    accessibilityLabel,
    isDisabled = false,
    customBorderColor,
    isShowNextVariant,
    variant = "tonal",
    type = "neutral",
    customIconColor,
    customTextColor,
    customLocalize,
    form = "cycle",
    customColor,
    customTheme,
    color,
    title,
    ...props
}) => {
    const {
        activeLocale,
        localeKeys
    } = NCoreUIKitLocalize.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const {
        iconSizes,
        radiuses,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const nativeTitles = useMemo(() => {
        return LOCALE_SWITCHER_DEFAULT_KEYS.reduce<Partial<Record<LocaleSwitcherDefaultKey, string>>>((currentNativeTitles, localeKey) => {
            const {
                translations
            } = NCoreUIKitLocalize.prepare({
                activeLocale: localeKey
            });

            return {
                ...currentNativeTitles,
                [localeKey]: translations ? translations[LOCALE_SWITCHER_TITLE_KEYS[localeKey]] : undefined
            };
        }, {});
    }, [
        localeKeys
    ]);

    const defaultVariants: Array<LocaleSwitchVariant> = LOCALE_SWITCHER_DEFAULT_KEYS.map((localeKey) => ({
        title: localize(LOCALE_SWITCHER_TITLE_KEYS[localeKey]),
        iconColor: LOCALE_SWITCHER_ICON_COLORS[localeKey],
        nativeTitle: nativeTitles[localeKey],
        localeKey
    }));

    const {
        accessibilityLabel: switcherAccessibilityLabel,
        variants: switcherVariants,
        displayVariant,
        nextVariant
    } = getSwitcherVariants<"localeKey", LocaleSwitchVariant>({
        isShowNextVariant: isShowNextVariant ?? isWorkWithNextShowSystem ?? false,
        title: localize("locale-switcher-title"),
        activeKey: activeLocale,
        variants: variantsProp,
        keyName: "localeKey",
        keys: localeKeys,
        defaultVariants,
        localize
    });

    useEffect(() => {
        if(isWorkWithNextShowSystem === undefined) {
            return;
        }

        warnOnce({
            message: "LocaleSwitcher: \"isWorkWithNextShowSystem\" is deprecated and will be removed in the next major version. Use \"isShowNextVariant\" instead."
        });
    }, [
        isWorkWithNextShowSystem
    ]);

    useEffect(() => {
        if(isWorkWithHighlightButton === undefined) {
            return;
        }

        warnOnce({
            message: "LocaleSwitcher: \"isWorkWithHighlightButton\" is deprecated and will be removed in the next major version. The switcher is a tonal neutral Button by default."
        });
    }, [
        isWorkWithHighlightButton
    ]);

    const isShowTitle = isShowTitleProp ?? form !== "cycle";

    const isMenuForm = form === "menu";

    const displayTitle = displayVariant && isShowTitle ? displayVariant.nativeTitle ?? displayVariant.title ?? String(displayVariant.localeKey) : undefined;

    const currentTitle = title ?? displayTitle;

    const buttonVariant = isWorkWithHighlightButton || variant === "filled" ? "tonal" : variant;

    const isTitleInAccessibilityLabel = !currentTitle || switcherAccessibilityLabel.includes(currentTitle);

    const languageProps = displayVariant && displayTitle && !title ? {
        accessibilityLanguage: String(displayVariant.localeKey),
        lang: String(displayVariant.localeKey)
    } : {};

    const handleSwitch = (localeKey: keyof NCoreUIKit.LocaleKey) => {
        NCoreUIKitLocalize.switch({
            localeKey
        });
    };

    const handleConfirmDirection = (variantItem: LocaleSwitchVariant, nextIsRTL: boolean) => {
        const directionName = localize(nextIsRTL ? "locale-switcher-direction-rtl" : "locale-switcher-direction-ltr");

        const localeName = variantItem.nativeTitle ?? variantItem.title ?? String(variantItem.localeKey);

        NCoreUIKitDialog.open({
            content: localize(Platform.OS === "web" ? "locale-switcher-direction-content" : "locale-switcher-direction-restart-content", [
                localeName,
                directionName
            ]),
            primaryButtonProps: {
                onPress: ({
                    closeAnimation
                }) => {
                    closeAnimation(() => handleSwitch(variantItem.localeKey));
                },
                title: localize("change-language")
            },
            title: localize("locale-switcher-direction-title"),
            contentJustify: "language-based",
            secondaryButtonProps: {
                onPress: ({
                    closeAnimation
                }) => {
                    closeAnimation();
                }
            },
            onOverlayPress: ({
                closeAnimation
            }) => {
                closeAnimation();
            },
            variant: "yes-no",
            customLocalize,
            customTheme
        });
    };

    const handleSelectVariant = (variantItem?: LocaleSwitchVariant) => {
        if(!variantItem || variantItem.localeKey === activeLocale) {
            return;
        }

        const nextState = NCoreUIKitLocalize.prepare({
            activeLocale: variantItem.localeKey
        });

        const nextIsRTL = !!nextState.isRTL;

        if(nextIsRTL === NCoreUIKitLocalize.state.isRTL) {
            handleSwitch(variantItem.localeKey);

            return;
        }

        handleConfirmDirection(variantItem, nextIsRTL);
    };

    const onPress = () => {
        handleSelectVariant(nextVariant);
    };

    const handleSelect = (key: string) => {
        handleSelectVariant(switcherVariants.find((variantItem) => String(variantItem.localeKey) === key));
    };

    const getVariantIcon = (variantItem: LocaleSwitchVariant, isFormIcon: boolean): NCoreUIKitIcon => {
        const LocaleVariantIcon: NCoreUIKitIcon = ({
            customColor: buttonIconColor,
            color: iconColorKey,
            size
        }) => {
            const {
                badge: badgeDynamicStyle
            } = useStyles({
                badgeColor: getLocaleSwitcherBadgeColor({
                    iconColor: variantItem.iconColor,
                    buttonIconColor,
                    isDisabled,
                    colors
                }),
                iconSizes,
                radiuses,
                size
            });

            if(variantItem.icon) {
                const VariantIcon = variantItem.icon;

                return <VariantIcon
                    customColor={getLocaleSwitcherIconColor({
                        isCustomIconColor: !!customIconColor || !!customTextColor,
                        iconColor: variantItem.iconColor,
                        buttonIconColor,
                        isDisabled,
                        colors
                    })}
                    color={iconColorKey}
                    size={size}
                />;
            }

            if(!isFormIcon && !currentTitle) {
                return <LanguagesIcon
                    color={buttonIconColor}
                    size={size}
                />;
            }

            return <View
                style={[
                    stylesheet.badge,
                    badgeDynamicStyle
                ]}
                aria-hidden={true}
            >
                <Text
                    style={stylesheet.badgeTitle}
                    customTheme={customTheme}
                    variant="labelSmallSize"
                    numberOfLines={1}
                    color="onPrimary"
                >
                    {
                        getLocaleSwitcherShortTitle({
                            shortTitle: variantItem.shortTitle,
                            localeKey: variantItem.localeKey
                        })
                    }
                </Text>
            </View>;
        };

        return LocaleVariantIcon;
    };

    const renderIcon: NCoreUIKitIcon = (iconProps) => {
        if(!displayVariant) {
            return <LanguagesIcon
                color={iconProps.customColor}
                size={iconProps.size}
            />;
        }

        return getVariantIcon(displayVariant, false)(iconProps);
    };

    const options = form === "cycle" ? [] : switcherVariants.map((variantItem) => ({
        title: variantItem.nativeTitle ?? variantItem.title ?? String(variantItem.localeKey),
        icon: getVariantIcon(variantItem, true),
        key: String(variantItem.localeKey),
        lang: String(variantItem.localeKey)
    }));

    const trigger = <Button
        {...props}
        {...languageProps}
        customColor={customColor || (displayVariant && displayVariant.backgroundColor ? displayVariant.backgroundColor : color)}
        accessibilityLabel={accessibilityLabel ?? (isTitleInAccessibilityLabel ? switcherAccessibilityLabel : undefined)}
        iconDirection={isMenuForm && currentTitle ? "end" : props.iconDirection}
        icon={isMenuForm && currentTitle ? PreferenceChevronIcon : renderIcon}
        customBorderColor={getSwitcherBorderColor({
            borderColor: displayVariant?.borderColor,
            customBorderColor,
            colors,
            color
        })}
        customIconColor={customIconColor}
        customTextColor={customTextColor}
        customTheme={customTheme}
        variant={buttonVariant}
        isDisabled={isDisabled}
        title={currentTitle}
        onPress={onPress}
        type={type}
    />;

    return <PreferenceForm
        size={props.size === "xSmall" ? "small" : props.size}
        title={localize("locale-switcher-title")}
        spreadBehaviour={props.spreadBehaviour}
        activeKey={String(activeLocale)}
        customLocalize={customLocalize}
        customTheme={customTheme}
        isShowTitle={isShowTitle}
        onSelect={handleSelect}
        isDisabled={isDisabled}
        testID={props.testID}
        style={props.style}
        trigger={trigger}
        options={options}
        form={form}
    />;
};
export default LocaleSwitcher;
